import React, { useRef } from 'react';
import { CircularProgress } from '@mui/material';
import styles from './ApplicationPhotoPopup.module.css';
import { useAuthFileUrl } from '../../components/patient-attachments/AuthFile';
import { MIN_CROP_SIZE, normalizeCrop } from '../../pages/appointment/components/applicationMap/applicationFigures';

const CORNERS = ['nw', 'ne', 'sw', 'se'];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round4 = (value) => Math.round(value * 10000) / 10000;

/** Novo recorte a partir do arrasto de uma alça (ou do interior, que move). */
function applyDrag(handle, origin, dx, dy) {
  const right = origin.x + origin.w;
  const bottom = origin.y + origin.h;
  if (handle === 'move') {
    return { ...origin, x: clamp(origin.x + dx, 0, 1 - origin.w), y: clamp(origin.y + dy, 0, 1 - origin.h) };
  }
  const next = { ...origin };
  if (handle === 'nw' || handle === 'sw') {
    next.x = clamp(origin.x + dx, 0, right - MIN_CROP_SIZE);
    next.w = right - next.x;
  } else {
    next.w = clamp(origin.w + dx, MIN_CROP_SIZE, 1 - origin.x);
  }
  if (handle === 'nw' || handle === 'ne') {
    next.y = clamp(origin.y + dy, 0, bottom - MIN_CROP_SIZE);
    next.h = bottom - next.y;
  } else {
    next.h = clamp(origin.h + dy, MIN_CROP_SIZE, 1 - origin.y);
  }
  return next;
}

/**
 * Enquadramento da foto de fundo: a foto inteira com um retângulo de recorte
 * por cima. Arrastar o interior move o recorte; arrastar um canto redimensiona.
 * O arquivo não muda: só o recorte (frações da foto) é gravado no mapa.
 *
 * `crop`: { x, y, w, h } em frações; `onChange(crop)` a cada movimento.
 */
const PhotoCropper = ({ attachmentId, crop, onChange }) => {
  const { url, loading, error } = useAuthFileUrl(attachmentId, 'full', !!attachmentId);
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const current = normalizeCrop(crop);

  function fractions(event) {
    const rect = boxRef.current.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height };
  }

  function startDrag(event, handle) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { handle, start: fractions(event), origin: current };
    boxRef.current.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event) {
    const drag = dragRef.current;
    if (!drag) return;
    const point = fractions(event);
    const next = applyDrag(drag.handle, drag.origin, point.x - drag.start.x, point.y - drag.start.y);
    onChange({ x: round4(next.x), y: round4(next.y), w: round4(next.w), h: round4(next.h) });
  }

  function endDrag() {
    dragRef.current = null;
  }

  if (error) return <div className={styles.cropMessage}>{error}</div>;
  if (loading || !url) {
    return (
      <div className={styles.cropMessage}>
        <CircularProgress size={20} />
        <span>Carregando foto...</span>
      </div>
    );
  }

  return (
    <div className={styles.cropStage}>
      <div
        ref={boxRef}
        className={styles.cropBox}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img className={styles.cropImage} src={url} alt="Foto do paciente" draggable={false} />
        <div
          className={styles.cropRect}
          style={{ left: `${current.x * 100}%`, top: `${current.y * 100}%`, width: `${current.w * 100}%`, height: `${current.h * 100}%` }}
          onPointerDown={(event) => startDrag(event, 'move')}
        >
          {CORNERS.map((corner) => (
            <span
              key={corner}
              className={`${styles.cropHandle} ${styles[`cropHandle_${corner}`]}`}
              onPointerDown={(event) => startDrag(event, corner)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhotoCropper;
